import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getUsername } from '../../util/dataExtractors';
import NamedLink from '../NamedLink/NamedLink';
import DashboardMobileSidebar from '../DashboardMobileSidebar/DashboardMobileSidebar';
import {
  BUYING,
  MESSAGES_LINK,
  OVERVIEW_LINK,
  SELLING,
  SIDE_LINKS,
  getSideForPath,
  isLinkCurrent,
} from '../DashboardSidebar/dashboardNav';
import { useDashboardSide, useOwnStorePaths } from '../DashboardSidebar/useDashboardSide';

import css from './DashboardMobileNav.module.css';

/**
 * Phone navigation for the dashboard: a Selling / Buying switch and a row of sections for that
 * side that scrolls sideways. Settings and everything else stay under "More".
 */
const DashboardMobileNav = () => {
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const { pathname } = useLocation();
  const ownStorePaths = useOwnStorePaths();
  const currentUser = useSelector(state => state.user.currentUser);
  const hasListings = useSelector(state => state.user.currentUserHasListings);
  const [side, setSide] = useDashboardSide();
  const currentRef = useRef(null);

  useEffect(() => {
    currentRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [pathname, side]);

  const chooseSide = nextSide => {
    if (nextSide === side) {
      return;
    }
    setSide(nextSide);
    // Leaving a page of the other side: show the Overview for the chosen side
    const pageSide = getSideForPath(pathname, ownStorePaths);
    if (pageSide && pageSide !== nextSide) {
      history.push(createResourceLocatorString('DashboardPage', routeConfiguration, {}, {}));
    }
  };

  const goToOwnStore = () => {
    const username = getUsername(currentUser);
    const userId = currentUser?.id?.uuid;
    if (!username && !userId) {
      return;
    }
    const routeName = username ? 'StorePage' : 'ProfilePage';
    const params = username ? { username } : { id: userId };
    history.push(createResourceLocatorString(routeName, routeConfiguration, params, {}));
  };

  const links = [OVERVIEW_LINK, ...SIDE_LINKS[side], MESSAGES_LINK];

  return (
    <div className={css.root}>
      <div
        className={css.switch}
        role="group"
        aria-label={intl.formatMessage({ id: 'DashboardNav.sideSwitchLabel' })}
      >
        {[SELLING, BUYING].map(option => (
          <button
            key={option}
            type="button"
            className={classNames(css.switchOption, { [css.switchOn]: side === option })}
            aria-pressed={side === option}
            onClick={() => chooseSide(option)}
          >
            <FormattedMessage id={`DashboardNav.side.${option}`} />
          </button>
        ))}
      </div>

      <nav
        className={css.pills}
        aria-label={intl.formatMessage({ id: 'DashboardSidebar.navigationAriaLabel' })}
      >
        {links.map(link => {
          const isCurrent = isLinkCurrent(link, pathname, ownStorePaths);
          const className = classNames(css.pill, { [css.pillOn]: isCurrent });
          const label = intl.formatMessage({ id: link.labelId });
          if (link.isOwnStore) {
            return hasListings ? (
              <button
                key={link.key}
                type="button"
                className={className}
                onClick={goToOwnStore}
                ref={isCurrent ? currentRef : null}
              >
                {label}
              </button>
            ) : null;
          }
          return (
            <NamedLink
              key={link.key}
              name={link.name}
              params={link.params}
              className={className}
              aria-current={isCurrent ? 'page' : undefined}
            >
              <span ref={isCurrent ? currentRef : null}>{label}</span>
            </NamedLink>
          );
        })}
        <div className={css.more}>
          <DashboardMobileSidebar />
        </div>
      </nav>
    </div>
  );
};

export default DashboardMobileNav;
